import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["aria-label", "aria-busy", "disabled"]

import { computed, ref, watch } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { DocCustomListsView } from '@/models/doc.custom.lists'
import type { DocsView, DocView } from '@/models/doc'
import type { UserDocListItem } from '@/models/interfaces'
import { Sentry } from '@/services/sentry'
import { appNotifications } from '@/services/notification.app'
import AddToListPopup from '@/app/components/doc/AddToListPopup.vue'

/**
 * The Save button for Save-for-Later.
 *
 * On the first click, if the doc is unsaved, save it to Want to Read.
 * If it is saved, open the destination menu.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'SaveButton',
  props: {
    doc: {},
    docsView: {}
  },
  setup(__props: any) {

const props = __props

const wait = useWait()
const view = ref<Raw<DocCustomListsView>>(
  new DocCustomListsView(props.doc, wait, props.docsView),
)
// The reading page replaces the DocView when a different guide is opened.
// This component stays mounted, so update here with the listsView too.
watch(
  () => props.doc,
  (doc) => {
    view.value = new DocCustomListsView(doc, wait, props.docsView)
    dismissSaveConfirmation()
  },
)
const isBusy = ref(false)

const icon = computed(() => (view.value.isDocInAnyList ? 'iflag2' : 'iflag'))
const label = computed(() => (view.value.isDocInAnyList ? 'Saved' : 'Save'))
const destinationMenu = ref<InstanceType<typeof AddToListPopup> | null>(null)
const saveButton = ref<HTMLElement | null>(null)

let confirmationId: number | null = null

function dismissSaveConfirmation(): void {
  if (confirmationId !== null) {
    appNotifications.dismiss(confirmationId)
    confirmationId = null
  }
}

async function openDestinationMenu(): Promise<void> {
  // The confirmation would cover the menu, so dismiss it.
  dismissSaveConfirmation()

  // The destination menu waits for its list data to load before it opens, so
  // we wait for it. Show the button as busy in the meantime.
  isBusy.value = true
  try {
    await destinationMenu.value?.show()
  } finally {
    isBusy.value = false
  }
}

async function onClick(): Promise<void> {
  if (isBusy.value) {
    return
  }

  // Saved items open the destination menu; only unsaved ones are saved.
  if (view.value.isDocInAnyList) {
    await openDestinationMenu()
    return
  }

  isBusy.value = true
  const savedView = view.value
  try {
    await savedView.setInWantToRead(true)
    // If the user switched docs while the save was happening, don't show a
    // confirmation. (If we showed it, "Change" would act on the new doc
    // instead.)
    if (view.value !== savedView) {
      return
    }
    confirmationId = appNotifications.unique({
      message: 'Saved to Want to Read',
      compact: true,
      actions: [{ label: 'Change', onClick: openDestinationMenu }],
    })
  } catch (error) {
    Sentry.captureException(error)
  } finally {
    isBusy.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "saveButton",
    ref: saveButton,
    class: "save-button",
    onClick: _withModifiers(onClick, ["stop","prevent"])
  }, [
    _renderSlot(_ctx.$slots, "default", {
      isSaved: view.value.isDocInAnyList,
      isBusy: isBusy.value,
      icon: icon.value,
      label: label.value
    }, () => [
      _createElementVNode("button", {
        class: _normalizeClass(["save-button__button", { 'save-button__button--saved': view.value.isDocInAnyList }]),
        "aria-label": label.value,
        "aria-busy": isBusy.value,
        disabled: isBusy.value
      }, [
        _createElementVNode("span", {
          class: _normalizeClass(["save-button__icon iconfont", icon.value]),
          "aria-hidden": "true"
        }, null, 2)
      ], 10, _hoisted_1)
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "#appRoot" }, [
      _createVNode(AddToListPopup, {
        ref_key: "destinationMenu",
        ref: destinationMenu,
        view: view.value,
        newStyle20321B: true,
        anchor: saveButton.value
      }, null, 8, ["view", "anchor"])
    ]))
  ], 512))
}
}

})